import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { Copy } from "@dreamhub/designsystem/icons";
import { Button } from "@/components/ui/button";
import Checkbox from "@/components/ui/checkbox";
import Input from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import Text from "@/components/ui/text";
import { Textarea } from "@/components/ui/textarea";
import {
  useSaveStripeConnection,
  useSaveStripeSecrets,
  useTestStripeConnection
} from "@/entities/stripeCheckout/hooks";
import {
  StripeConnectionSettings,
  StripeSecretsUpdate
} from "@/entities/stripeCheckout/types";
import { webFormErrorDetail } from "@/entities/webForm/hooks";
import { RECORDING_EXCLUDED_CLASSES } from "@/pages/settings/components/integrations/ApiKeyConnectModal";
import {
  ASYNC_PAYMENT_SUCCEEDED,
  STRIPE_CHECKOUT_PREFIX,
  SecretKind,
  connectionErrors,
  eventTypesFor,
  isValidSecret,
  parseLines,
  sameList,
  stripeWebhookUrl
} from "@/pages/settings/components/integrations/stripeCheckout/stripeCheckoutOptions";
import Section, {
  SETTINGS_CARD_CONTAINER_CLASS
} from "@/pages/settings/components/sections/Section";
import { useAuth } from "@/services/auth/AuthContext";
import { cn } from "@/utils/utils";

const PREFIX = `${STRIPE_CHECKOUT_PREFIX}.connection`;
const SECRET_KINDS: SecretKind[] = ["webhookSigningSecret", "restrictedApiKey"];
const FIELD_CLASSNAME =
  "w-[240px] rounded-lg bg-surface-field-default shadow-field outline-none";

interface StripeConnectionSectionProps {
  settings: StripeConnectionSettings | undefined;
  isLoading: boolean;
  isError: boolean;
}

export const StripeConnectionSection: React.FC<
  StripeConnectionSectionProps
> = ({ settings, isLoading, isError }) => {
  const { t } = useTranslation();
  const { tenantId } = useAuth();
  const saveConnection = useSaveStripeConnection();
  const saveSecrets = useSaveStripeSecrets();
  const testConnection = useTestStripeConnection();
  const [secrets, setSecrets] = useState<Record<SecretKind, string>>({
    webhookSigningSecret: "",
    restrictedApiKey: ""
  });
  const [isSavingSecrets, setIsSavingSecrets] = useState(false);
  const [secretsError, setSecretsError] = useState<string | null>(null);
  const [testMessage, setTestMessage] = useState<{
    ok: boolean;
    text: string;
  } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  const [delayedPayments, setDelayedPayments] = useState(
    settings?.eventTypes.includes(ASYNC_PAYMENT_SUCCEEDED) ?? false
  );
  const [connect, setConnect] = useState(settings?.connect ?? false);
  const [accountsText, setAccountsText] = useState(
    (settings?.connectedAccounts ?? []).join("\n")
  );
  // Show what was saved once it loads or changes, replacing any older draft.
  const saved = settings
    ? JSON.stringify([
        settings.connect,
        settings.connectedAccounts,
        settings.eventTypes
      ])
    : null;
  const [synced, setSynced] = useState(saved);
  if (saved !== synced) {
    setSynced(saved);
    if (settings) {
      setDelayedPayments(settings.eventTypes.includes(ASYNC_PAYMENT_SUCCEEDED));
      setConnect(settings.connect);
      setAccountsText(settings.connectedAccounts.join("\n"));
    }
  }

  const update = {
    connect,
    connectedAccounts: parseLines(accountsText),
    eventTypes: eventTypesFor(delayedPayments)
  };
  const errors = connectionErrors(update);
  const disabled = !settings || saveConnection.isPending || isSavingSecrets;
  const isDirty =
    !!settings &&
    (update.connect !== settings.connect ||
      !sameList(update.connectedAccounts, settings.connectedAccounts) ||
      !sameList(
        [...update.eventTypes].sort(),
        [...settings.eventTypes].sort()
      ));
  const canSave = isDirty && errors.length === 0 && !disabled;

  const enteredSecrets = SECRET_KINDS.filter((kind) => secrets[kind].trim());
  const invalidSecrets = enteredSecrets.filter(
    (kind) => !isValidSecret(kind, secrets[kind].trim())
  );
  const canSaveSecrets =
    !!settings &&
    enteredSecrets.length > 0 &&
    invalidSecrets.length === 0 &&
    !isSavingSecrets;

  const webhookUrl = tenantId ? stripeWebhookUrl(tenantId) : "";

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast.success(t(`${PREFIX}.webhookUrl.copied`));
    } catch {
      // Clipboard API unavailable in non-secure contexts.
      toast.error(t(`${PREFIX}.webhookUrl.copyError`));
    }
  };

  const handleSaveSecrets = async () => {
    setSecretsError(null);
    setIsSavingSecrets(true);
    const update: StripeSecretsUpdate = Object.fromEntries(
      enteredSecrets.map((kind) => [kind, secrets[kind].trim()])
    );
    const result = await saveSecrets(update);
    setIsSavingSecrets(false);
    if (result.status === "saved") {
      // Cleared at once: a secret is never shown back, nor kept in the page.
      setSecrets({ webhookSigningSecret: "", restrictedApiKey: "" });
      setTestMessage(null);
      toast.success(t(`${PREFIX}.secrets.saved`));
      return;
    }
    const message =
      (result.status === "rejected" && result.message) ||
      t(`${PREFIX}.secrets.saveError`);
    setSecretsError(message);
    toast.error(message);
  };

  const handleTest = () => {
    setTestMessage(null);
    testConnection.mutate(
      {},
      {
        onSuccess: (result) =>
          setTestMessage({
            ok: result.status === "ok",
            text: t(`${PREFIX}.test.results.${result.status}`)
          }),
        onError: () =>
          setTestMessage({ ok: false, text: t(`${PREFIX}.test.error`) })
      }
    );
  };

  const handleSave = () => {
    setSaveError(null);
    saveConnection.mutate(
      { data: update },
      {
        onSuccess: () => toast.success(t(`${PREFIX}.saved`)),
        onError: (error) => {
          const message = webFormErrorDetail(error) ?? t(`${PREFIX}.saveError`);
          setSaveError(message);
          toast.error(message);
        }
      }
    );
  };

  const secretInput = (kind: SecretKind) => {
    const isSet =
      kind === "webhookSigningSecret"
        ? settings?.hasWebhookSigningSecret
        : settings?.hasRestrictedApiKey;
    return {
      title: t(`${PREFIX}.secrets.${kind}.title`),
      subTitle: isSet
        ? t(`${PREFIX}.secrets.${kind}.set`)
        : t(`${PREFIX}.secrets.${kind}.subtitle`),
      customAction: (
        <Input
          type="password"
          data-test={`stripe-checkout-${kind}`}
          aria-label={t(`${PREFIX}.secrets.${kind}.title`)}
          placeholder={
            isSet
              ? t(`${PREFIX}.secrets.replacePlaceholder`)
              : t(`${PREFIX}.secrets.${kind}.placeholder`)
          }
          value={secrets[kind]}
          onChange={(event) => {
            const value = event.target.value;
            setSecrets((current) => ({ ...current, [kind]: value }));
            setSecretsError(null);
          }}
          hasError={invalidSecrets.includes(kind)}
          disabled={!settings || isSavingSecrets}
          autoComplete="off"
          spellCheck={false}
          autoCorrect="off"
          autoCapitalize="off"
          data-1p-ignore
          data-lpignore="true"
          className={RECORDING_EXCLUDED_CLASSES}
          containerClassName="w-[240px]"
        />
      )
    };
  };

  const subTitle = isError
    ? t(`${STRIPE_CHECKOUT_PREFIX}.loadError`)
    : t(`${PREFIX}.subtitle`);

  return (
    <>
      <Section
        title={t(`${PREFIX}.title`)}
        subTitle={subTitle}
        isLoading={isLoading}
      >
        <div
          className={cn(SETTINGS_CARD_CONTAINER_CLASS, "gap-2 p-4")}
          data-test="stripe-checkout-webhook-card"
        >
          <div className="flex items-center gap-2">
            <Text variant="bodySecondary" className="flex-1">
              {t(`${PREFIX}.webhookUrl.instructions`)}
            </Text>
            <Button
              variant="secondary"
              className="h-7 px-1.5"
              onClick={handleCopy}
              disabled={!webhookUrl}
              data-test="stripe-checkout-copy-webhook-url"
            >
              <Copy className="h-4 w-4" />
              {t(`${PREFIX}.webhookUrl.copy`)}
            </Button>
          </div>
          <pre
            className="w-full overflow-auto whitespace-pre-wrap break-all rounded-lg border border-border-border1 bg-surface-field-default p-2 font-mono text-sm text-secondary"
            data-test="stripe-checkout-webhook-url"
          >
            {webhookUrl}
          </pre>
          {settings && (
            <Text
              variant="body"
              className={settings.configured ? "text-positive" : "text-warning"}
              data-test="stripe-checkout-connection-status"
            >
              {settings.configured
                ? t(`${PREFIX}.status.configured`)
                : t(`${PREFIX}.status.notConfigured`)}
            </Text>
          )}
        </div>
      </Section>
      <Section
        title={t(`${PREFIX}.secrets.title`)}
        subTitle={t(`${PREFIX}.secrets.subtitle`)}
        isLoading={isLoading}
        items={SECRET_KINDS.map(secretInput)}
      >
        {(invalidSecrets.length > 0 || secretsError) && (
          <div
            className="flex flex-col gap-1"
            role="alert"
            data-test="stripe-checkout-secrets-error"
          >
            {invalidSecrets.map((kind) => (
              <Text key={kind} variant="smBodyError">
                {t(`${PREFIX}.secrets.${kind}.invalid`)}
              </Text>
            ))}
            {secretsError && <Text variant="smBodyError">{secretsError}</Text>}
          </div>
        )}
        <div className="flex items-center justify-end gap-4">
          {testMessage && (
            <Text
              variant="body"
              role="status"
              className={cn(
                "flex-1",
                testMessage.ok ? "text-positive" : "text-error"
              )}
              data-test="stripe-checkout-test-result"
            >
              {testMessage.text}
            </Text>
          )}
          <Button
            variant="secondary"
            onClick={handleTest}
            disabled={
              !settings?.hasRestrictedApiKey ||
              testConnection.isPending ||
              isSavingSecrets
            }
            data-test="stripe-checkout-test"
          >
            {t(`${PREFIX}.test.action`)}
          </Button>
          <Button
            onClick={() => void handleSaveSecrets()}
            disabled={!canSaveSecrets}
            data-test="stripe-checkout-save-secrets"
          >
            {t(`${PREFIX}.secrets.save`)}
          </Button>
        </div>
      </Section>
      <Section
        title={t(`${PREFIX}.events.title`)}
        subTitle={t(`${PREFIX}.events.subtitle`)}
        isLoading={isLoading}
        items={[
          {
            title: t(`${PREFIX}.events.delayedPayments.title`),
            subTitle: t(`${PREFIX}.events.delayedPayments.subtitle`),
            customAction: (
              <Checkbox
                data-test="stripe-checkout-delayed-payments"
                aria-label={t(`${PREFIX}.events.delayedPayments.title`)}
                checked={delayedPayments}
                onCheckedChange={(checked) => {
                  setDelayedPayments(checked);
                  setSaveError(null);
                }}
                disabled={disabled}
              />
            )
          },
          {
            title: t(`${PREFIX}.events.connect.title`),
            subTitle: t(`${PREFIX}.events.connect.subtitle`),
            customAction: (
              <Switch
                data-test="stripe-checkout-connect"
                aria-label={t(`${PREFIX}.events.connect.title`)}
                checked={connect}
                onCheckedChange={(checked) => {
                  setConnect(checked);
                  setSaveError(null);
                }}
                disabled={disabled}
              />
            )
          },
          // Shown either way, so a list that would block a save is never hidden.
          {
            title: t(`${PREFIX}.events.accounts.title`),
            subTitle: t(`${PREFIX}.events.accounts.subtitle`),
            customAction: (
              <Textarea
                data-test="stripe-checkout-connected-accounts"
                aria-label={t(`${PREFIX}.events.accounts.title`)}
                placeholder={t(`${PREFIX}.events.accounts.placeholder`)}
                value={accountsText}
                onChange={(event) => {
                  setAccountsText(event.target.value);
                  setSaveError(null);
                }}
                rows={3}
                disabled={disabled}
                className={FIELD_CLASSNAME}
              />
            )
          }
        ]}
      >
        {(errors.length > 0 || saveError) && (
          <div
            className="flex flex-col gap-1"
            role="alert"
            data-test="stripe-checkout-connection-error"
          >
            {errors.map((error) => (
              <Text key={error} variant="smBodyError">
                {t(`${PREFIX}.errors.${error}`)}
              </Text>
            ))}
            {saveError && <Text variant="smBodyError">{saveError}</Text>}
          </div>
        )}
        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            disabled={!canSave}
            data-test="stripe-checkout-save-connection"
          >
            {t(`${PREFIX}.save`)}
          </Button>
        </div>
      </Section>
    </>
  );
};
